import { storeToRefs } from 'pinia';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import multiviewUrlIdxNameToNumber from '@/helpers/multiview_url_idx_name_to_number';
import useSettingsStore from '@/stores/settings';

const multiviewDefaultParams = { width: 640, q: 30, fps: 12, idx: 220 };

function buildMultiviewUrl(station, params = {}) {
  const width = params.width ?? multiviewDefaultParams.width;
  const q = params.q ?? multiviewDefaultParams.q;
  const fps = params.fps ?? multiviewDefaultParams.fps;
  const idx = multiviewUrlIdxNameToNumber(params.idx ?? multiviewDefaultParams.idx);
  return station?.nateApiUrl
    ? `${station.nateApiUrl}/streams/multiview?width=${width}&q=${q}&fps=${fps}&idx=${idx}`
    : null;
}

export default function useStation({ station, previewIdx, monitorIdx }) {
  const { t } = useI18n();
  const settingsStore = useSettingsStore();

  const stationLabel = computed(() => {
    const stationValue = toValue(station);

    if (stationValue.status !== 'ok' && !stationValue.online) {
      return `#${stationValue.number}: ${t(`station_offline`)}`;
    }

    const label = [
      stationValue?.stationName !== `#${stationValue.number}` ? stationValue?.stationName : null,
      stationValue?.trackName,
    ]
      .filter((v) => !!v)
      .join(' - ');
    if (!label) return `#${stationValue.number}`;

    return `#${stationValue.number}: ${label}`;
  });

  const { previewWidth, previewEncoderQuality, previewFps } = storeToRefs(settingsStore);
  const previewUrl = computed(() =>
    toValue(station)
      ? buildMultiviewUrl(toValue(station), {
          width: previewWidth.value,
          q: previewEncoderQuality.value,
          fps: previewFps.value,
          idx: toValue(previewIdx),
        })
      : null,
  );

  const { monitorWidth, monitorEncoderQuality, monitorFps } = storeToRefs(settingsStore);
  const monitorUrl = computed(() =>
    toValue(station)
      ? buildMultiviewUrl(toValue(station), {
          width: monitorWidth.value,
          q: monitorEncoderQuality.value,
          fps: monitorFps.value,
          idx: toValue(monitorIdx),
        })
      : null,
  );

  const statusLabel = computed(() => t(`station_status_label.${toValue(station).status}`));
  const statusMessage = computed(() => {
    const stationValue = toValue(station);

    if (!stationValue.online && stationValue.status !== 'ok') return t('station_offline_message');

    return t(`station_status_message.${stationValue.status}`);
  });
  const statusColorClass = computed(() => {
    const stationValue = toValue(station);

    if (!stationValue.online && stationValue.status !== 'ok') return 'tw-text-state-err';
    if (stationValue.status !== 'ok') return 'tw-text-state-warn';

    return '';
  });

  return { station, stationLabel, previewUrl, monitorUrl, statusLabel, statusMessage, statusColorClass };
}
